import { useMemo, useState } from 'react'

import { F0Dialog, F0Select, F0SelectItemProps } from '@factorialco/f0-react'
import {
  AtsJobApplicationAddToVacancyDataDocument,
  AtsJobApplicationAddToVacancyItemFragment,
  AtsJobApplicationAddToVacancyFragment,
  AtsJobApplicationAssignVacancyDocument,
  AtsJobApplicationAssignVacancyMutation,
  AtsJobApplicationHeaderDocument,
} from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useQuery } from 'modules/core/hooks/useQuery'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

interface Props {
  application: AtsJobApplicationAddToVacancyFragment
  onClose: () => void
}

export const AddToVacancyModal = ({ application, onClose }: Props) => {
  const { employee: sessionEmployee } = useCurrentSession()
  const [selectedVacancyId, setSelectedVacancyId] = useState<string | undefined>(undefined)

  const { loading, data } = useQuery(AtsJobApplicationAddToVacancyDataDocument)

  const [assignVacancy, { client }] = useMutation(AtsJobApplicationAssignVacancyDocument, {
    extractorFn: (result: AtsJobApplicationAssignVacancyMutation) =>
      result.atsMutations.assignApplicationAtsVacancy,
  })

  const vacancies = useMemo(() => data?.ats.vacanciesConnection.nodes ?? [], [data])

  const options: F0SelectItemProps<string>[] = useMemo(() => {
    const toOption = (
      vacancy: AtsJobApplicationAddToVacancyItemFragment
    ): F0SelectItemProps<string> => {
      const description =
        [
          vacancy.location?.name,
          [vacancy.jobCatalogTreeRole?.name, vacancy.jobCatalogTreeLevel?.name]
            .filter(Boolean)
            .join(' ') || undefined,
          vacancy.team?.name,
        ]
          .filter(Boolean)
          .join(' / ') || undefined

      return {
        value: String(vacancy.id),
        label: vacancy.name,
        description,
        tag: vacancy.lineManager?.fullName,
      }
    }

    const assignedToMe: AtsJobApplicationAddToVacancyItemFragment[] = []
    const allVacancies: AtsJobApplicationAddToVacancyItemFragment[] = []

    for (const vacancy of vacancies) {
      if (vacancy.atsVacancyHiringManagers.some((hm) => hm.id === sessionEmployee.id)) {
        assignedToMe.push(vacancy)
      } else {
        allVacancies.push(vacancy)
      }
    }

    const sortByJobPosting = (
      a: AtsJobApplicationAddToVacancyItemFragment,
      b: AtsJobApplicationAddToVacancyItemFragment
    ) => {
      const aLinked = a.jobPosting?.id === application.jobPosting.id ? 0 : 1
      const bLinked = b.jobPosting?.id === application.jobPosting.id ? 0 : 1
      return aLinked - bLinked
    }

    assignedToMe.sort(sortByJobPosting)
    allVacancies.sort(sortByJobPosting)

    const result: F0SelectItemProps<string>[] = []

    if (assignedToMe.length > 0) {
      result.push({
        value: '__assigned_to_me_header__',
        label: i18n.t('ats.application_details.header.add_to_vacancy.assigned_to_me'),
        disabled: true,
      })
      result.push(...assignedToMe.map(toOption))
    }

    if (allVacancies.length > 0) {
      if (assignedToMe.length > 0) {
        result.push({ type: 'separator' })
      }
      result.push({
        value: '__all_vacancies_header__',
        label: i18n.t('ats.application_details.header.add_to_vacancy.all_vacancies'),
        disabled: true,
      })
      result.push(...allVacancies.map(toOption))
    }

    return result
  }, [vacancies, sessionEmployee.id, application.jobPosting.id])

  const handleAssign = async () => {
    if (!selectedVacancyId) return

    const result = await assignVacancy({
      variables: {
        applicationId: application.id,
        id: selectedVacancyId,
      },
    })

    const handled = handleResult(result, () => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('ats.application_details.header.add_to_vacancy.error_message'),
      })
    })

    if (handled.success) {
      void client.refetchQueries({ include: [AtsJobApplicationHeaderDocument] })
      app.setFlash({
        type: 'correct',
        message: i18n.t('ats.application_details.header.add_to_vacancy.success_message'),
      })
    }

    onClose()
  }

  return (
    <F0Dialog
      title={i18n.t('ats.application_details.header.add_to_vacancy.title')}
      isOpen
      onClose={onClose}
      primaryAction={{
        label: i18n.t('ats.application_details.header.add_to_vacancy.assign_button'),
        onClick: handleAssign,
        disabled: !selectedVacancyId,
      }}
      secondaryAction={{
        label: i18n.t('ats.application_details.header.add_to_vacancy.cancel_button'),
        onClick: onClose,
      }}
    >
      <F0Select
        label={i18n.t('ats.application_details.header.add_to_vacancy.select_label')}
        name="vacancy"
        placeholder={i18n.t('ats.application_details.header.add_to_vacancy.select_placeholder')}
        size="md"
        value={selectedVacancyId}
        onChange={(value: string) => setSelectedVacancyId(value)}
        options={options}
        showSearchBox
        searchBoxPlaceholder={i18n.t(
          'ats.application_details.header.add_to_vacancy.search_placeholder'
        )}
        searchEmptyMessage={i18n.t(
          'ats.application_details.header.add_to_vacancy.search_no_results'
        )}
        readonly={loading}
      />
    </F0Dialog>
  )
}
